'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  question: string;
  answer: string;
  category: string;
}

interface FAQSectionProps {
  faqs: FAQ[];
}

export default function FAQSection({ faqs }: FAQSectionProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const categories = ['All', ...Array.from(new Set(faqs.map((faq) => faq.category)))];

  const filteredFAQs =
    selectedCategory === 'All' ? faqs : faqs.filter((faq) => faq.category === selectedCategory);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  if (!isHydrated) {
    return (
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-background">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-1/2 mx-auto"></div>
            <div className="h-4 bg-muted rounded w-3/4 mx-auto"></div>
            <div className="space-y-3 pt-6">
              <div className="h-16 bg-muted rounded"></div>
              <div className="h-16 bg-muted rounded"></div>
              <div className="h-16 bg-muted rounded"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-background">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-headline font-bold text-textPrimary mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-textSecondary max-w-2xl mx-auto">
            Find quick answers to common questions about our services and support.
          </p>
        </div>

        <div className="mb-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full font-body font-medium text-sm transition-all duration-300 ${
                  selectedCategory === category
                    ? 'bg-primary text-primary-foreground shadow-md'
                    : 'bg-muted text-textSecondary hover:bg-muted/80'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          {filteredFAQs.map((faq, index) => (
            <div
              key={index}
              className="bg-card rounded-lg border border-border overflow-hidden transition-all duration-300 hover:shadow-md"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-muted/50 transition-colors duration-300"
              >
                <div className="flex-1 pr-4">
                  <h3 className="text-lg font-headline font-bold text-textPrimary mb-1">
                    {faq.question}
                  </h3>
                  <span className="inline-block px-2 py-1 rounded-full bg-primary/10 text-primary text-xs font-body font-medium">
                    {faq.category}
                  </span>
                </div>
                <Icon
                  name="ChevronDownIcon"
                  size={24}
                  className={`text-textSecondary flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {openIndex === index && (
                <div className="px-6 pb-4 pt-2">
                  <p className="text-textSecondary font-body leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {filteredFAQs.length === 0 && (
          <div className="text-center py-12">
            <Icon name="QuestionMarkCircleIcon" size={48} className="text-textSecondary mx-auto mb-4" />
            <p className="text-textSecondary font-body">No FAQs found in this category.</p>
          </div>
        )}

        <div className="mt-12 bg-primary/5 border border-primary/20 rounded-lg p-6 text-center">
          <h3 className="text-xl font-headline font-bold text-textPrimary mb-2">
            Still Have Questions?
          </h3>
          <p className="text-textSecondary font-body mb-4">
            Can't find the answer you're looking for? Our support team is here to help.
          </p>
          <a
            href="#contact-form"
            className="inline-flex items-center px-6 py-3 bg-primary text-primary-foreground rounded-lg font-body font-semibold hover:bg-primary/90 transition-all duration-300"
          >
            Contact Support
            <Icon name="ArrowRightIcon" size={18} className="ml-2" />
          </a>
        </div>
      </div>
    </section>
  );
}